import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Leaf,
  ChevronRight,
  ChevronLeft,
  Check,
  Sparkles,
  Heart,
  ShieldCheck,
  HeartHandshake,
  Sun,
  Anchor,
  CloudOff,
  Pause,
  FileText,
} from 'lucide-react';
import { useJournal } from '../hooks/useJournal';
import SEO from '../components/SEO';

/* --------------------------- Prompts --------------------------- */
/*
 * Each step is either:
 *  - { kind: 'affirmation', ... } a screen that just reassures
 *  - { kind: 'prompt', ... } a screen with a written response
 *  - { kind: 'chips', ... } a screen with multi-select feelings
 *  - { kind: 'final', ... } the closing reassurance screen
 */
const STEPS = [
  {
    kind: 'affirmation',
    icon: ShieldCheck,
    eyebrow: 'BEFORE WE BEGIN',
    title: 'You are safe right now.',
    body:
      'Before we start, take a moment. Look around you. Name three things you can see. Feel your feet on the floor and the air on your skin.\n\nThe past is not happening now. In this moment you are here, in this room, with this page. There is no rush. There is no wrong way to do this.',
    nextLabel: 'I\u2019m ready',
  },
  {
    kind: 'prompt',
    icon: Anchor,
    eyebrow: 'STEP 1 \u00B7 GROUNDING',
    title: 'How is your body feeling?',
    helper:
      'Just notice. Tight chest, heavy shoulders, fluttery stomach, foggy head, calm. Whatever is there is okay.',
    placeholder: 'Right now my body feels\u2026',
    fieldKey: 'body',
    optional: true,
  },
  {
    kind: 'prompt',
    icon: Heart,
    eyebrow: 'STEP 2 \u00B7 WHY TODAY',
    title: 'What brought you here today?',
    helper:
      'A sentence is enough. There\u2019s no need to explain it to anyone \u2014 this is only for you.',
    placeholder: 'I came here because\u2026',
    fieldKey: 'why',
    optional: true,
  },
  {
    kind: 'chips',
    icon: Sparkles,
    eyebrow: 'STEP 3 \u00B7 NAMING IT',
    title: 'What feelings are you carrying?',
    helper:
      'Tap anything that fits \u2014 you can choose as many as you like, or none. Naming feelings out loud (even silently) is an act of healing.',
    fieldKey: 'feelings',
    options: [
      'Numb',
      'Angry',
      'Frightened',
      'Sad',
      'Ashamed',
      'Guilty',
      'Confused',
      'Exhausted',
      'Hopeful',
      'Stronger than yesterday',
      'Disgusted',
      'Empty',
      'Alone',
      'Relieved',
      'Anxious',
      'Calm',
    ],
    optional: true,
  },
  {
    kind: 'affirmation',
    icon: ShieldCheck,
    eyebrow: 'STEP 4 \u00B7 READ THIS SLOWLY',
    title: 'It was not your fault.',
    body:
      "What happened to you is not because of what you wore, where you were, what you drank, who you trusted, what you said, what you didn't say, what your body did, or what it didn't do.\n\nFreezing, fawning, complying, going quiet \u2014 these are survival responses. They are not consent. They are your nervous system trying to keep you alive.\n\nThe person who hurt you made a choice. You did not.",
    nextLabel: 'Continue',
  },
  {
    kind: 'prompt',
    icon: HeartHandshake,
    eyebrow: 'STEP 5 \u00B7 GENTLENESS',
    title: 'What would you say to a friend going through this?',
    helper:
      'If your closest friend had been through what you have, what would you want them to hear? Try writing it as if you were speaking to them \u2014 then notice that the same words are for you too.',
    placeholder: 'Dear friend\u2026',
    fieldKey: 'to_friend',
    optional: true,
  },
  {
    kind: 'prompt',
    icon: Sun,
    eyebrow: 'STEP 6 \u00B7 ONE SMALL THING',
    title: 'One small kindness you can offer yourself today.',
    helper:
      'It does not have to be big. A glass of water. A nap. A cancelled plan. Five minutes outside. A favourite song. Saying "no". You deserve small good things, today, exactly as you are.',
    placeholder: 'Today I will\u2026',
    fieldKey: 'kindness',
    optional: true,
  },
  {
    kind: 'final',
    icon: Heart,
    eyebrow: 'YOU MADE IT THROUGH',
    title: 'You are believed.\nYou are not alone.',
    body:
      'Whatever you wrote, whatever you skipped, however you got here \u2014 you did something brave by sitting with yourself.\n\nHealing is not linear. There will be good days and harder days, and both are part of it. Specialist UK support is here whenever you are ready.\n\nTake what helped today. Leave what didn\u2019t.',
  },
];

const TOTAL_INPUT_STEPS = STEPS.filter(
  (s) => s.kind === 'prompt' || s.kind === 'chips',
).length;

/* --------------------------- Helpers --------------------------- */
const STORAGE_KEY = 'tfi_guided_draft_v1';

function loadDraft() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveDraft(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function clearDraft() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function compileEntry(answers) {
  // Build a readable journal entry from the answers.
  const parts = [];
  parts.push('Guided journal reflection');
  parts.push('');
  if (answers.body) {
    parts.push('How my body felt:');
    parts.push(answers.body);
    parts.push('');
  }
  if (answers.why) {
    parts.push('Why I came here today:');
    parts.push(answers.why);
    parts.push('');
  }
  if (Array.isArray(answers.feelings) && answers.feelings.length > 0) {
    parts.push('Feelings I named: ' + answers.feelings.join(', '));
    parts.push('');
  }
  if (answers.to_friend) {
    parts.push('What I would say to a friend (and to me):');
    parts.push(answers.to_friend);
    parts.push('');
  }
  if (answers.kindness) {
    parts.push('One small kindness for myself today:');
    parts.push(answers.kindness);
    parts.push('');
  }
  parts.push('— I read: "It was not my fault. I am believed. I am not alone."');
  return parts.join('\n').trim();
}

/* --------------------------- UI bits --------------------------- */

function Chip({ label, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1.5 rounded-full font-bold transition-all"
      style={{
        background: selected ? 'var(--tf-purple)' : '#fff',
        color: selected ? '#fff' : 'var(--tf-text-secondary)',
        border: `1.5px solid ${selected ? 'var(--tf-purple)' : 'var(--tf-card-border)'}`,
        fontSize: 12.5,
        cursor: 'pointer',
      }}
      data-testid={`chip-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {label}
    </button>
  );
}

/* --------------------------- Main page --------------------------- */

export default function GuidedJournal() {
  const navigate = useNavigate();
  const { addEntry } = useJournal();

  const draft = useMemo(() => loadDraft(), []);
  const [idx, setIdx] = useState(draft?.idx ?? 0);
  const [answers, setAnswers] = useState(draft?.answers ?? {});
  const [saved, setSaved] = useState(false);

  // Persist progress between page reloads/tabs (sessionStorage only).
  useEffect(() => {
    saveDraft({ idx, answers });
  }, [idx, answers]);

  const step = STEPS[idx];
  const inputIndex = useMemo(() => {
    let n = 0;
    for (let i = 0; i <= idx; i += 1) {
      if (STEPS[i].kind === 'prompt' || STEPS[i].kind === 'chips') n += 1;
    }
    return n;
  }, [idx]);

  const progressPct = Math.round(((idx + 1) / STEPS.length) * 100);

  const setField = (key, value) =>
    setAnswers((prev) => ({ ...prev, [key]: value }));

  const toggleChip = (key, value) => {
    setAnswers((prev) => {
      const arr = Array.isArray(prev[key]) ? prev[key] : [];
      const next = arr.includes(value)
        ? arr.filter((x) => x !== value)
        : [...arr, value];
      return { ...prev, [key]: next };
    });
  };

  const goBack = () => setIdx((i) => Math.max(0, i - 1));
  const goNext = () => setIdx((i) => Math.min(STEPS.length - 1, i + 1));

  const finishAndSave = () => {
    const content = compileEntry(answers);
    if (!content.trim()) {
      // nothing to save
      clearDraft();
      setSaved(true);
      setTimeout(() => navigate('/app/journal'), 1200);
      return;
    }
    addEntry({ content, mood: null });
    clearDraft();
    setSaved(true);
    setTimeout(() => navigate('/app/journal'), 1200);
  };

  const StepIcon = step.icon || Leaf;
  const isInput = step.kind === 'prompt' || step.kind === 'chips';
  const currentValue =
    step.fieldKey && step.kind === 'prompt'
      ? answers[step.fieldKey] || ''
      : '';
  const currentChips =
    step.fieldKey && step.kind === 'chips'
      ? Array.isArray(answers[step.fieldKey])
        ? answers[step.fieldKey]
        : []
      : [];

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10" data-testid="guided-journal-screen">
      <SEO
        title="Guided Journal \u2014 Trauma-informed Reflection"
        description="A calm, step-by-step trauma-informed journal that reminds you you're safe, that what happened was not your fault, and gently walks you through naming feelings and finding small acts of self-kindness."
        path="/app/guided-journal"
      />

      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <Link
          to="/app/journal"
          className="inline-flex items-center gap-1 font-bold"
          style={{ color: 'var(--tf-text)', textDecoration: 'none', fontSize: 13 }}
          data-testid="guided-back-to-journal"
        >
          <ChevronLeft size={18} /> Free journal
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href="https://customer-assets.emergentagent.com/job_justice-pathway-uk/artifacts/lq2f708l_The%20Full%20Impact%20Campaign%20%E2%80%94%20Guided%20Journal%20Workbook%20Colour.pdf%20%282%29.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="guided-pdf-colour"
            className="tf-no-print inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-bold"
            style={{
              background: 'var(--tf-purple)',
              color: '#fff',
              border: 'none',
              fontSize: 12,
              textDecoration: 'none',
            }}
          >
            <FileText size={13} />
            PDF — Colour
          </a>
          <a
            href="https://customer-assets.emergentagent.com/job_justice-pathway-uk/artifacts/ieg473n4_The%20Full%20Impact%20Campaign%20%E2%80%94%20Guided%20Journal%20Workbook%20Simple%20Black%20and%20White.pdf.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="guided-pdf-bw"
            className="tf-no-print inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-bold"
            style={{
              background: '#fff',
              color: 'var(--tf-purple)',
              border: '1px solid var(--tf-purple-light)',
              fontSize: 12,
              textDecoration: 'none',
            }}
          >
            <FileText size={13} />
            PDF — Black &amp; White
          </a>
        </div>
      </div>

      {/* "Use offline" reassurance */}
      <div
        className="rounded-2xl p-4 mb-6 tf-no-print"
        style={{
          background: 'var(--tf-pink-soft)',
          border: '1px solid var(--tf-purple-light)',
        }}
      >
        <p
          className="font-body font-bold"
          style={{
            fontSize: 10.5,
            letterSpacing: '0.18em',
            color: 'var(--tf-purple-dark)',
          }}
        >
          TAKE IT WITH YOU
        </p>
        <p
          className="font-body mt-2"
          style={{ fontSize: 12.5, color: 'var(--tf-text)', lineHeight: 1.6 }}
        >
          <strong>Want a print-ready workbook?</strong> Tap{' '}
          <em>PDF &mdash; Colour</em> for the fully branded lavender version, or{' '}
          <em>PDF &mdash; Black &amp; White</em> for an ink-friendly print version. Both open
          on your device so you can save them, print them, or share them with someone
          supporting you.
        </p>
      </div>

      {/* Privacy note */}
      <div
        className="rounded-2xl p-3 flex items-center gap-3 mb-6"
        style={{
          background: 'var(--tf-bg-secondary)',
          border: '1px solid var(--tf-card-border)',
        }}
      >
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: '#fff', color: 'var(--tf-purple)' }}
        >
          <CloudOff size={15} />
        </div>
        <p
          className="font-body"
          style={{ fontSize: 12, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
        >
          Nothing you write here is uploaded. You can stop, leave, or close this tab at any
          time — your draft stays only on this device.
        </p>
      </div>

      {/* Progress */}
      <div className="mb-6" data-testid="guided-progress">
        <div className="flex items-baseline justify-between mb-2">
          <p
            className="font-body font-bold"
            style={{ fontSize: 10.5, letterSpacing: '0.18em', color: 'var(--tf-purple)' }}
          >
            STEP {idx + 1} OF {STEPS.length}
          </p>
          {isInput && (
            <p
              className="font-body"
              style={{ fontSize: 11, color: 'var(--tf-text-muted)' }}
            >
              {inputIndex} of {TOTAL_INPUT_STEPS} prompts
            </p>
          )}
        </div>
        <div
          className="rounded-full overflow-hidden"
          style={{ height: 6, background: 'var(--tf-bg-secondary)' }}
        >
          <div
            style={{
              width: `${progressPct}%`,
              height: '100%',
              background: 'linear-gradient(90deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
              transition: 'width 0.4s ease',
            }}
          />
        </div>
      </div>

      {/* Step card */}
      <div
        className="tf-card p-7 sm:p-9 tf-fade-up"
        key={idx}
        data-testid={`guided-step-${idx}`}
      >
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
          style={{
            background:
              step.kind === 'affirmation' || step.kind === 'final'
                ? 'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)'
                : 'var(--tf-bg-secondary)',
            color:
              step.kind === 'affirmation' || step.kind === 'final'
                ? '#fff'
                : 'var(--tf-purple)',
          }}
        >
          <StepIcon size={20} />
        </div>

        <p className="eyebrow">{step.eyebrow}</p>
        <h1
          className="font-heading mt-3"
          style={{
            fontSize: 'clamp(22px, 3vw, 28px)',
            lineHeight: 1.2,
            color: 'var(--tf-text)',
            letterSpacing: '-0.01em',
            whiteSpace: 'pre-line',
          }}
        >
          {step.title}
        </h1>

        {step.body && (
          <p
            className="font-body mt-4"
            style={{
              fontSize: 14.5,
              color: 'var(--tf-text-secondary)',
              lineHeight: 1.7,
              whiteSpace: 'pre-line',
            }}
          >
            {step.body}
          </p>
        )}

        {step.helper && (
          <p
            className="font-body mt-3"
            style={{
              fontSize: 13,
              color: 'var(--tf-text-secondary)',
              lineHeight: 1.6,
            }}
          >
            {step.helper}
          </p>
        )}

        {step.kind === 'prompt' && (
          <textarea
            value={currentValue}
            onChange={(e) => setField(step.fieldKey, e.target.value)}
            placeholder={step.placeholder || ''}
            rows={6}
            data-testid={`guided-input-${step.fieldKey}`}
            className="w-full mt-5 p-4 rounded-xl outline-none font-body"
            style={{
              background: 'var(--tf-bg)',
              border: '1px solid var(--tf-card-border)',
              fontSize: 15,
              lineHeight: 1.6,
              color: 'var(--tf-text)',
              resize: 'vertical',
              minHeight: 120,
            }}
          />
        )}

        {step.kind === 'chips' && (
          <div className="mt-5 flex flex-wrap gap-2">
            {step.options.map((opt) => (
              <Chip
                key={opt}
                label={opt}
                selected={currentChips.includes(opt)}
                onClick={() => toggleChip(step.fieldKey, opt)}
              />
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="mt-7 flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={goBack}
            disabled={idx === 0}
            data-testid="guided-back"
            className="inline-flex items-center gap-1 px-3.5 py-2 rounded-full font-bold"
            style={{
              background: '#fff',
              color: 'var(--tf-text-secondary)',
              border: '1px solid var(--tf-card-border)',
              fontSize: 12.5,
              cursor: idx === 0 ? 'not-allowed' : 'pointer',
              opacity: idx === 0 ? 0.4 : 1,
            }}
          >
            <ChevronLeft size={14} />
            Back
          </button>

          <div className="flex items-center gap-2 flex-wrap">
            {step.optional && (
              <button
                type="button"
                onClick={goNext}
                data-testid="guided-skip"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-bold"
                style={{
                  background: 'transparent',
                  color: 'var(--tf-text-muted)',
                  border: 'none',
                  fontSize: 12.5,
                  cursor: 'pointer',
                }}
              >
                <Pause size={13} />
                Skip
              </button>
            )}
            {step.kind !== 'final' && (
              <button
                type="button"
                onClick={goNext}
                data-testid="guided-next"
                className="tf-btn-primary"
              >
                {step.nextLabel || 'Next'} <ChevronRight size={15} />
              </button>
            )}
            {step.kind === 'final' && (
              <button
                type="button"
                onClick={finishAndSave}
                data-testid="guided-finish"
                className="tf-btn-primary"
                disabled={saved}
                style={{ opacity: saved ? 0.7 : 1 }}
              >
                {saved ? (
                  <>
                    <Check size={15} /> Saved
                  </>
                ) : (
                  <>
                    <Check size={15} /> Save to journal
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Quick exit note */}
      <p
        className="font-body mt-6 text-center"
        style={{ fontSize: 11.5, color: 'var(--tf-text-muted)', lineHeight: 1.55 }}
      >
        You can stop at any moment. Tap <strong>Exit Now</strong> at the bottom-left of the
        page to leave the site instantly. Your draft is held only in this browser tab.
      </p>
      <p
        className="font-body mt-3 text-center"
        style={{ fontSize: 10.5, color: 'var(--tf-text-muted)', lineHeight: 1.55 }}
      >
        Guided journal &copy; {new Date().getFullYear()} Angela Moreton &middot; The Full
        Impact&trade; &middot; Personal use only &middot; All rights reserved.
      </p>
    </div>
  );
}
